import React, { useState, useEffect } from 'react';
import {
  getKnowledge, saveKnowledge, deleteKnowledge,
  getBehaviors, getPatterns, seedKnowledgeScript,
  getKnowledgeChunks, saveKnowledgeChunk, deleteKnowledgeChunk,
  syncChunksToRules,
} from '../../services/storage';
import { THEME } from '../../styles/theme';
import { PINE_SCRIPTS } from '../../data/pinescripts';
import { useJarvis } from '../../hooks/useJarvis';

// ── ICT concept reference library (static) ────────────────────────────
const ICT_LIBRARY = [
  {
    concept: 'Order Block (OB)',
    emoji: '🏛️',
    color: THEME.colors.blue,
    summary: 'Last opposing candle before a significant move. Smart money placed orders here.',
    bullish: 'Last bearish candle before a strong bullish move — BUY at this zone.',
    bearish: 'Last bullish candle before a strong bearish move — SELL at this zone.',
    xauusd: 'XAU/USD OBs on H1/H4 are most reliable. Combine with liquidity sweep for A+ grade.',
    rule: 'Only trade OBs that are UNMITIGATED (price has not returned to them yet).',
  },
  {
    concept: 'Fair Value Gap (FVG)',
    emoji: '🕳️',
    color: THEME.colors.gold,
    summary: 'Price imbalance — a 3-candle pattern where middle candle leaves a gap. Price magnetically returns to fill it.',
    bullish: 'Candle 1 high to Candle 3 low = bullish FVG. Price returns to buy here.',
    bearish: 'Candle 1 low to Candle 3 high = bearish FVG. Price returns to sell here.',
    xauusd: 'XAU/USD fills FVGs 70–80% of the time. Use as targets AND entry zones.',
    rule: 'Stronger FVG = deeper imbalance (bigger gap) = stronger magnet pull.',
  },
  {
    concept: 'BSL / SSL Liquidity',
    emoji: '💧',
    color: THEME.colors.green,
    summary: 'Resting stop orders above highs (BSL) and below lows (SSL). Smart money sweeps these first.',
    bullish: 'SSL swept (stop hunt below lows) → smart money now LONG → price runs up.',
    bearish: 'BSL swept (stop hunt above highs) → smart money now SHORT → price drops.',
    xauusd: 'Equal highs and equal lows on H4/D1 = major liquidity pools. Always note them.',
    rule: 'After a liquidity sweep, look for entry in the OPPOSITE direction of the sweep.',
  },
  {
    concept: 'BOS / CHoCH',
    emoji: '🔀',
    color: '#ff9900',
    summary: 'Market structure — BOS confirms trend continuation. CHoCH is the first sign of reversal.',
    bullish: 'BOS: price breaks above a prior swing high in uptrend = trend continues up.',
    bearish: 'CHoCH: in uptrend, price breaks below a Higher Low = structure shifting bearish.',
    xauusd: 'Use H4 BOS/CHoCH for daily bias. Use H1 for entry timing after CHoCH.',
    rule: 'CHoCH = early warning only. Wait for second confirmation before trading reversal.',
  },
  {
    concept: 'AMD — Power of 3',
    emoji: '⚡',
    color: THEME.colors.blue,
    summary: 'Accumulation → Manipulation → Distribution. ~70% of trading days follow this cycle.',
    bullish: 'Accumulate range → Manipulate DOWN (sweep SSL) → Distribute UP (real move).',
    bearish: 'Accumulate range → Manipulate UP (sweep BSL) → Distribute DOWN (real move).',
    xauusd: 'London open = manipulation phase most days. NY open = distribution begins.',
    rule: 'If you see London spike down followed by strong reversal — that is AMD in action.',
  },
  {
    concept: 'OTE Zone (Fibonacci)',
    emoji: '📐',
    color: THEME.colors.green,
    summary: 'Optimal Trade Entry — the 0.618 to 0.786 Fibonacci retracement zone.',
    bullish: 'In uptrend: draw Fib from swing low to swing high. Buy zone = 0.618–0.786.',
    bearish: 'In downtrend: draw Fib from swing high to swing low. Sell zone = 0.618–0.786.',
    xauusd: 'OTE + H4 OB = highest probability entry. This is institutional entry zone.',
    rule: 'OTE alone is not enough. Must combine with OB or FVG + HTF bias alignment.',
  },
  {
    concept: 'Killzones (IST)',
    emoji: '🕐',
    color: THEME.colors.red,
    summary: 'Specific time windows when institutional activity is highest. Best setups happen here.',
    bullish: 'London (09:00–14:00 IST): Best for BUY setups after SSL sweep.',
    bearish: 'London+NY (14:00–20:00 IST): Continuation or reversal with highest volume.',
    xauusd: 'Asia (00:00–05:00 IST) = range building. Avoid trading. Study levels instead.',
    rule: 'Never trade outside killzones unless setup is A+ grade with all factors confirmed.',
  },
  {
    concept: 'Premium / Discount',
    emoji: '📊',
    color: THEME.colors.gold,
    summary: '50% Fibonacci of any range = equilibrium. Below = discount (buy zone). Above = premium (sell zone).',
    bullish: 'Discount zone = below 50% of range. BUY in discount. Never buy at premium.',
    bearish: 'Premium zone = above 50% of range. SELL in premium. Never sell at discount.',
    xauusd: 'Apply to weekly and daily ranges for highest-timeframe directional bias.',
    rule: 'If JARVIS says BUY but price is at premium — WAIT for pullback to discount first.',
  },
];

const CONFIDENCE_COLORS = {
  LOW:       { color: THEME.colors.textMuted, bg: 'rgba(255,255,255,0.05)' },
  MEDIUM:    { color: THEME.colors.gold,      bg: 'rgba(255,215,0,0.08)' },
  HIGH:      { color: THEME.colors.blue,      bg: 'rgba(0,212,255,0.08)' },
  CONFIRMED: { color: THEME.colors.green,     bg: 'rgba(0,255,136,0.08)' },
  STRONG:    { color: THEME.colors.blue,      bg: 'rgba(0,212,255,0.08)' },
};

const S = {
  page: { padding: '20px', maxWidth: '100%', paddingBottom: '40px' },
  header: { marginBottom: '20px' },
  title: { fontSize: '22px', fontWeight: '700', color: THEME.colors.blue, letterSpacing: '2px', fontFamily: THEME.fonts.hud },
  subtitle: { fontSize: '13px', color: THEME.colors.textMuted, marginTop: '4px' },
  tabs: { display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' },
  tab: (active) => ({
    padding: '7px 16px', borderRadius: '20px', cursor: 'pointer', fontSize: '13px', fontWeight: '600',
    border: active ? `1px solid ${THEME.colors.blue}` : '1px solid rgba(255,255,255,0.1)',
    background: active ? 'rgba(0,212,255,0.1)' : 'transparent',
    color: active ? THEME.colors.blue : THEME.colors.textMuted, transition: 'all 0.15s',
  }),
  card: {
    background: 'rgba(255,255,255,0.03)',
    borderTop: '1px solid rgba(255,255,255,0.07)',
    borderRight: '1px solid rgba(255,255,255,0.07)',
    borderBottom: '1px solid rgba(255,255,255,0.07)',
    borderLeft: '1px solid rgba(255,255,255,0.07)',
    borderRadius: '12px', padding: '16px', marginBottom: '12px',
  },
  badge: (color, bg) => ({
    padding: '2px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '700',
    color, background: bg, display: 'inline-block',
  }),
  textarea: {
    width: '100%', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '8px', color: THEME.colors.text, fontSize: '14px', padding: '10px 12px',
    resize: 'vertical', minHeight: '80px', outline: 'none', fontFamily: THEME.fonts.body,
    lineHeight: '1.6', boxSizing: 'border-box',
  },
  input: {
    width: '100%', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '8px', color: THEME.colors.text, fontSize: '14px', padding: '9px 12px',
    outline: 'none', fontFamily: THEME.fonts.body, boxSizing: 'border-box',
  },
  btn: (variant) => ({
    padding: '9px 18px', borderRadius: '8px', border: 'none', cursor: 'pointer',
    fontSize: '13px', fontWeight: '700', fontFamily: THEME.fonts.body,
    background: variant === 'primary' ? THEME.colors.blue
              : variant === 'danger'  ? 'rgba(255,51,51,0.15)'
              : 'rgba(255,255,255,0.07)',
    color: variant === 'primary' ? '#000'
         : variant === 'danger'  ? THEME.colors.red
         : THEME.colors.textMuted,
  }),
  sectionLabel: {
    fontSize: '10px', fontWeight: '700', letterSpacing: '2px', color: THEME.colors.textMuted,
    fontFamily: THEME.fonts.hud, marginBottom: '8px', textTransform: 'uppercase',
  },
  emptyState: {
    textAlign: 'center', padding: '40px 20px', color: THEME.colors.textMuted, fontSize: '14px',
  },
  ictCard: (color) => ({
    background: `${color}08`, border: `1px solid ${color}22`,
    borderRadius: '12px', padding: '16px', marginBottom: '12px',
  }),
  ictTitle: (color) => ({
    fontSize: '16px', fontWeight: '700', color, fontFamily: THEME.fonts.hud,
    letterSpacing: '1px', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px',
  }),
  ictRow: { fontSize: '13px', color: THEME.colors.text, lineHeight: '1.7', marginBottom: '6px' },
  ictLabel: { color: THEME.colors.textMuted, fontSize: '11px', letterSpacing: '1px', display: 'block', marginBottom: '2px' },
  searchInput: {
    width: '100%', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '8px', color: THEME.colors.text, fontSize: '14px', padding: '9px 12px',
    outline: 'none', fontFamily: THEME.fonts.body, boxSizing: 'border-box', marginBottom: '14px',
  },
};

// ── Add Knowledge Form ────────────────────────────────────────────────
function AddKnowledgeForm({ onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: '', type: 'personal', raw_content: '' });

  const handleSave = () => {
    if (!form.title.trim() || !form.raw_content.trim()) return;
    saveKnowledge(form);
    setForm({ title: '', type: 'personal', raw_content: '' });
    setOpen(false);
    onSaved();
  };

  if (!open) return (
    <button style={{ ...S.btn('primary'), marginBottom: '16px' }} onClick={() => setOpen(true)}>
      + Add Knowledge
    </button>
  );

  return (
    <div style={{ ...S.card, borderColor: 'rgba(0,212,255,0.2)', marginBottom: '16px' }}>
      <div style={S.sectionLabel}>NEW KNOWLEDGE ITEM</div>
      <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
        <input
          style={{ ...S.input, flex: 2 }}
          placeholder="Title (e.g. London Killzone OB Strategy)"
          value={form.title}
          onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
        />
        <select
          style={{ ...S.input, flex: 1 }}
          value={form.type}
          onChange={e => setForm(p => ({ ...p, type: e.target.value }))}
        >
          <option value="personal">Personal Observation</option>
          <option value="ict_concept">ICT Concept</option>
          <option value="price_action">Price Action</option>
          <option value="psychology">Psychology</option>
          <option value="resource">External Resource</option>
        </select>
      </div>
      <textarea
        style={{ ...S.textarea, marginBottom: '10px' }}
        placeholder="Paste content, notes, or describe what you learned. JARVIS will extract rules and behaviors automatically when you teach it via Daily Recap or Claude Bridge."
        value={form.raw_content}
        onChange={e => setForm(p => ({ ...p, raw_content: e.target.value }))}
      />
      <div style={{ display: 'flex', gap: '8px' }}>
        <button style={S.btn('primary')} onClick={handleSave}>Save</button>
        <button style={S.btn()} onClick={() => setOpen(false)}>Cancel</button>
      </div>
    </div>
  );
}

// ── Knowledge Item Card ───────────────────────────────────────────────
function KnowledgeCard({ item, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div style={S.card}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px' }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '15px', fontWeight: '700', color: THEME.colors.text }}>{item.title}</span>
            <span style={S.badge(THEME.colors.blue, 'rgba(0,212,255,0.08)')}>{item.type}</span>
            <span style={{ fontSize: '11px', color: THEME.colors.textMuted }}>{item.date_added?.slice(0,10)}</span>
          </div>
          {expanded && (
            <div style={{ fontSize: '13px', color: THEME.colors.textMuted, lineHeight: '1.7', marginTop: '8px', whiteSpace: 'pre-wrap' }}>
              {item.raw_content}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
          <button style={{ ...S.btn(), padding: '5px 10px', fontSize: '12px' }} onClick={() => setExpanded(p => !p)}>
            {expanded ? '▲' : '▼'}
          </button>
          <button style={{ ...S.btn('danger'), padding: '5px 10px', fontSize: '12px' }} onClick={() => onDelete(item.id)}>
            ✕
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Behaviors Tab ─────────────────────────────────────────────────────
function BehaviorsTab() {
  const [filter, setFilter] = useState('ALL');
  const [search, setSearch] = useState('');
  const behaviors = getBehaviors();

  const filters = ['ALL', 'CONFIRMED', 'HIGH', 'MEDIUM', 'LOW'];
  const visible  = behaviors
    .filter(b => filter === 'ALL' || b.confidence === filter)
    .filter(b => !search || b.title.toLowerCase().includes(search.toLowerCase()) || b.description?.toLowerCase().includes(search.toLowerCase()));

  return (
    <>
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
        {filters.map(f => (
          <button key={f} style={S.tab(filter === f)} onClick={() => setFilter(f)}>
            {f} {f !== 'ALL' ? `(${behaviors.filter(b => b.confidence === f).length})` : `(${behaviors.length})`}
          </button>
        ))}
      </div>
      <input style={S.searchInput} placeholder="Search behaviors..." value={search} onChange={e => setSearch(e.target.value)} />
      {visible.length === 0 && (
        <div style={S.emptyState}>
          {behaviors.length === 0
            ? 'No behaviors saved yet. JARVIS learns behaviors from daily recaps and chat.'
            : 'No behaviors match this filter.'}
        </div>
      )}
      {visible.map(b => {
        const conf = CONFIDENCE_COLORS[b.confidence] || CONFIDENCE_COLORS.LOW;
        return (
          <div key={b.id} style={{ ...S.card, borderLeft: `3px solid ${conf.color}` }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px', marginBottom: '8px' }}>
              <span style={{ fontSize: '15px', fontWeight: '700', color: THEME.colors.text, flex: 1 }}>{b.title}</span>
              <span style={S.badge(conf.color, conf.bg)}>{b.confidence}</span>
            </div>
            {b.description && (
              <div style={{ fontSize: '13px', color: THEME.colors.textMuted, lineHeight: '1.7', marginBottom: '8px' }}>{b.description}</div>
            )}
            <div style={{ display: 'flex', gap: '10px', fontSize: '11px', color: THEME.colors.textMuted, flexWrap: 'wrap' }}>
              {b.timeframe && <span>⏱ {b.timeframe}</span>}
              {b.learned_from && <span>📌 {b.learned_from}</span>}
              {b.date_observed && <span>📅 {b.date_observed?.slice(0,10)}</span>}
            </div>
          </div>
        );
      })}
    </>
  );
}

// ── Patterns Tab ──────────────────────────────────────────────────────
function PatternsTab() {
  const [search, setSearch] = useState('');
  const patterns = getPatterns();
  const visible  = patterns.filter(p =>
    !search || p.title.toLowerCase().includes(search.toLowerCase()) || p.description?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <>
      <input style={S.searchInput} placeholder="Search patterns..." value={search} onChange={e => setSearch(e.target.value)} />
      {visible.length === 0 && (
        <div style={S.emptyState}>
          {patterns.length === 0
            ? 'No patterns saved yet. Patterns are extracted from daily recaps and chat with JARVIS.'
            : 'No patterns match this search.'}
        </div>
      )}
      {visible.map(p => {
        const conf = CONFIDENCE_COLORS[p.confidence] || CONFIDENCE_COLORS.LOW;
        return (
          <div key={p.id} style={{ ...S.card, borderLeft: `3px solid ${conf.color}` }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px', marginBottom: '8px' }}>
              <span style={{ fontSize: '15px', fontWeight: '700', color: THEME.colors.text, flex: 1 }}>{p.title}</span>
              <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                {p.setup_type && <span style={S.badge(THEME.colors.gold, 'rgba(255,215,0,0.08)')}>{p.setup_type}</span>}
                <span style={S.badge(conf.color, conf.bg)}>{p.confidence}</span>
              </div>
            </div>
            {p.description && (
              <div style={{ fontSize: '13px', color: THEME.colors.textMuted, lineHeight: '1.7', marginBottom: '8px' }}>{p.description}</div>
            )}
            <div style={{ display: 'flex', gap: '14px', fontSize: '12px', color: THEME.colors.textMuted, flexWrap: 'wrap' }}>
              {p.entry_condition && <span>📍 Entry: {p.entry_condition}</span>}
              {p.target && <span>🎯 Target: {p.target}</span>}
            </div>
          </div>
        );
      })}
    </>
  );
}

// ── ICT Library Tab ───────────────────────────────────────────────────
function ICTLibraryTab() {
  const [expanded, setExpanded] = useState(null);

  return (
    <>
      <div style={{ fontSize: '13px', color: THEME.colors.textMuted, marginBottom: '14px', lineHeight: '1.6' }}>
        JARVIS's built-in ICT/SMC knowledge. These concepts are loaded into every analysis automatically.
      </div>
      {ICT_LIBRARY.map(item => (
        <div
          key={item.concept}
          style={S.ictCard(item.color)}
          onClick={() => setExpanded(expanded === item.concept ? null : item.concept)}
        >
          <div style={{ ...S.ictTitle(item.color), cursor: 'pointer', justifyContent: 'space-between' }}>
            <span>{item.emoji} {item.concept}</span>
            <span style={{ fontSize: '14px' }}>{expanded === item.concept ? '▲' : '▼'}</span>
          </div>
          <div style={{ fontSize: '13px', color: THEME.colors.textMuted, lineHeight: '1.6' }}>
            {item.summary}
          </div>
          {expanded === item.concept && (
            <div style={{ marginTop: '14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <div>
                <span style={S.ictLabel}>BULLISH APPLICATION</span>
                <div style={S.ictRow}>{item.bullish}</div>
              </div>
              <div>
                <span style={S.ictLabel}>BEARISH APPLICATION</span>
                <div style={S.ictRow}>{item.bearish}</div>
              </div>
              <div>
                <span style={S.ictLabel}>XAU/USD SPECIFIC</span>
                <div style={{ ...S.ictRow, color: item.color }}>{item.xauusd}</div>
              </div>
              <div style={{
                background: `${item.color}0d`, border: `1px solid ${item.color}22`,
                borderRadius: '8px', padding: '10px 12px',
              }}>
                <span style={S.ictLabel}>JARVIS RULE</span>
                <div style={{ ...S.ictRow, color: THEME.colors.text, fontStyle: 'italic' }}>"{item.rule}"</div>
              </div>
            </div>
          )}
        </div>
      ))}
    </>
  );
}

// ── Add Script Form ────────────────────────────────────────────────────────
function AddScriptForm({ onDone }) {
  const { extractScript, isThinking } = useJarvis();
  const [open,   setOpen]   = useState(false);
  const [title,  setTitle]  = useState('');
  const [code,   setCode]   = useState('');
  const [result, setResult] = useState(null);
  const [err,    setErr]    = useState('');

  const reset = () => { setTitle(''); setCode(''); setResult(null); setErr(''); };

  const handleExtract = async () => {
    if (!code.trim()) { setErr('Script code paste kara pehle.'); return; }
    if (!title.trim()) { setErr('Script la naam dya.'); return; }
    setErr('');
    setResult(null);
    try {
      const r = await extractScript(code, title);
      setResult(r);
      onDone?.();
    } catch (e) {
      setErr('JARVIS analysis failed: ' + e.message);
    }
  };

  if (!open) return (
    <button
      style={{ ...S.btn('primary'), marginBottom: '16px', fontSize: '14px', padding: '10px 20px' }}
      onClick={() => setOpen(true)}
    >
      + Navi Script Add Kara
    </button>
  );

  return (
    <div style={{ ...S.card, borderColor: 'rgba(0,212,255,0.25)', marginBottom: '20px', borderLeft: '3px solid rgba(0,212,255,0.5)' }}>
      <div style={S.sectionLabel}>NAVI PINE SCRIPT ADD KARA</div>

      <input
        style={{ ...S.input, marginBottom: '10px' }}
        placeholder="Script naam (e.g. SMC + OB Fib Strategy)"
        value={title}
        onChange={e => setTitle(e.target.value)}
      />

      <textarea
        style={{ ...S.textarea, minHeight: '180px', marginBottom: '10px', fontFamily: 'monospace', fontSize: '12px' }}
        placeholder="TradingView Pine Script code ithe paste kara..."
        value={code}
        onChange={e => setCode(e.target.value)}
      />

      {err && (
        <div style={{ color: THEME.colors.red, fontSize: '13px', marginBottom: '10px' }}>{err}</div>
      )}

      {result && (
        <div style={{ background: 'rgba(0,255,136,0.06)', border: '1px solid rgba(0,255,136,0.2)', borderRadius: '10px', padding: '14px', marginBottom: '12px' }}>
          <div style={{ fontSize: '15px', fontWeight: '700', color: THEME.colors.green, marginBottom: '8px' }}>
            ✅ JARVIS ne {result.rulesCount} rules save kele ({result.totalExtracted} extracted)
          </div>
          <div style={{ fontSize: '13px', color: THEME.colors.text, lineHeight: '1.7', marginBottom: '10px' }}>
            {result.summary}
          </div>
          {result.core_concept && (
            <div style={{ fontSize: '12px', color: THEME.colors.blue, marginBottom: '8px' }}>
              💡 Core Concept: {result.core_concept}
            </div>
          )}
          {result.rules?.length > 0 && (
            <div>
              <div style={{ fontSize: '11px', fontWeight: '700', color: THEME.colors.textMuted, letterSpacing: '1.5px', marginBottom: '6px' }}>
                EXTRACTED RULES:
              </div>
              {result.rules.map((r, i) => (
                <div key={i} style={{ background: 'rgba(255,255,255,0.03)', borderRadius: '6px', padding: '8px 10px', marginBottom: '6px', fontSize: '12px' }}>
                  <span style={{ color: THEME.colors.blue, fontWeight: '700' }}>{i + 1}. {r.title}</span>
                  <span style={{ color: r.action === 'BUY' ? THEME.colors.green : r.action === 'SELL' ? THEME.colors.red : THEME.colors.textMuted, marginLeft: '8px', fontWeight: '700' }}>
                    [{r.action}]
                  </span>
                  <div style={{ color: THEME.colors.textMuted, marginTop: '3px' }}>{r.condition}</div>
                </div>
              ))}
            </div>
          )}
          {result.confluence_tip && (
            <div style={{ fontSize: '12px', color: THEME.colors.gold, marginTop: '8px', fontStyle: 'italic' }}>
              🎯 {result.confluence_tip}
            </div>
          )}
          <div style={{ fontSize: '12px', color: THEME.colors.textMuted, marginTop: '10px' }}>
            He rules aata JARVIS Rule Book madhe save ahet. Chat madhe discuss kela ki JARVIS te vaparnar.
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        <button
          style={{ ...S.btn('primary'), opacity: isThinking ? 0.6 : 1 }}
          onClick={handleExtract}
          disabled={isThinking}
        >
          {isThinking ? '🤖 JARVIS Analyze karto...' : '🤖 JARVIS Analyze & Rules Save Kara'}
        </button>
        <button style={S.btn()} onClick={() => { reset(); setOpen(false); }}>
          Cancel
        </button>
      </div>
    </div>
  );
}

// ── Scripts Tab ────────────────────────────────────────────────────────────
function ScriptsTab({ onScriptAdded }) {
  const [expanded, setExpanded] = useState(null);
  const [copied,   setCopied]   = useState(null);

  const handleCopy = (id, code) => {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(id);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  return (
    <>
      <AddScriptForm onDone={onScriptAdded} />

      <div style={{ fontSize: '14px', color: THEME.colors.textMuted, marginBottom: '16px', lineHeight: '1.65' }}>
        TradingView Pine Scripts — JARVIS memory मध्ये saved आहेत. Copy करा आणि TradingView Pine Editor मध्ये paste करा.
      </div>

      {PINE_SCRIPTS.map(script => (
        <div key={script.seed_id} style={{
          background: 'rgba(33,87,243,0.05)', border: '1px solid rgba(33,87,243,0.2)',
          borderLeft: '3px solid #2157f3', borderRadius: '12px',
          padding: '18px', marginBottom: '16px',
        }}>
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px', flexWrap: 'wrap', gap: '10px' }}>
            <div>
              <div style={{ fontSize: '16px', fontWeight: '800', color: '#2157f3', marginBottom: '4px' }}>
                📜 {script.title}
              </div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <span style={{ padding: '2px 9px', borderRadius: '20px', fontSize: '11px', fontWeight: '700', background: 'rgba(33,87,243,0.12)', color: '#2157f3' }}>
                  {script.platform}
                </span>
                <span style={{ padding: '2px 9px', borderRadius: '20px', fontSize: '11px', fontWeight: '700', background: 'rgba(255,215,0,0.1)', color: THEME.colors.gold }}>
                  {script.category}
                </span>
                <span style={{ padding: '2px 9px', borderRadius: '20px', fontSize: '11px', color: THEME.colors.textMuted, background: 'rgba(255,255,255,0.05)' }}>
                  {script.version}
                </span>
              </div>
            </div>
            <button
              onClick={() => handleCopy(script.seed_id, script.code)}
              style={{
                padding: '8px 18px', borderRadius: '8px', border: 'none', cursor: 'pointer',
                background: copied === script.seed_id ? 'rgba(0,255,136,0.15)' : 'rgba(33,87,243,0.18)',
                color: copied === script.seed_id ? '#00ff88' : '#2157f3',
                fontWeight: '700', fontSize: '13px', transition: 'all 0.2s',
              }}
            >
              {copied === script.seed_id ? '✅ Copied!' : '📋 Copy Code'}
            </button>
          </div>

          {/* Marathi description */}
          <div style={{ fontSize: '14px', color: THEME.colors.text, lineHeight: '1.7', marginBottom: '12px', background: 'rgba(255,255,255,0.03)', borderRadius: '8px', padding: '12px' }}>
            {script.marathi_desc}
          </div>

          {/* Usage steps */}
          <div style={{ fontSize: '12px', fontWeight: '700', color: THEME.colors.textMuted, letterSpacing: '1.5px', marginBottom: '8px' }}>
            TradingView वर कसे लावायचे:
          </div>
          <div style={{ marginBottom: '14px' }}>
            {script.usage_steps.map((step, i) => (
              <div key={i} style={{ display: 'flex', gap: '10px', marginBottom: '6px', alignItems: 'flex-start' }}>
                <div style={{
                  width: '20px', height: '20px', borderRadius: '50%', flexShrink: 0,
                  background: 'rgba(33,87,243,0.2)', border: '1px solid rgba(33,87,243,0.4)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '11px', fontWeight: '800', color: '#2157f3',
                }}>{i + 1}</div>
                <div style={{ fontSize: '13px', color: THEME.colors.text, lineHeight: '1.55', paddingTop: '2px' }}>{step}</div>
              </div>
            ))}
          </div>

          {/* JARVIS integration tip */}
          <div style={{
            background: 'rgba(0,212,255,0.06)', border: '1px solid rgba(0,212,255,0.15)',
            borderRadius: '8px', padding: '10px 14px', marginBottom: '14px',
            fontSize: '13px', color: THEME.colors.text, lineHeight: '1.65',
          }}>
            <strong style={{ color: THEME.colors.blue }}>🤖 JARVIS Integration:</strong><br />
            {script.jarvis_integration}
          </div>

          {/* Code preview toggle */}
          <button
            onClick={() => setExpanded(expanded === script.seed_id ? null : script.seed_id)}
            style={{
              background: 'transparent', border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '8px', padding: '7px 14px', color: THEME.colors.textMuted,
              cursor: 'pointer', fontSize: '13px', width: '100%', textAlign: 'left',
            }}
          >
            {expanded === script.seed_id ? '▲ Code लपवा' : '▼ Full Code बघा'}
          </button>

          {expanded === script.seed_id && (
            <div style={{ marginTop: '10px', position: 'relative' }}>
              <button
                onClick={() => handleCopy(script.seed_id + '_2', script.code)}
                style={{
                  position: 'absolute', top: '8px', right: '8px', zIndex: 1,
                  padding: '4px 10px', borderRadius: '6px', border: 'none',
                  background: 'rgba(33,87,243,0.3)', color: '#2157f3',
                  cursor: 'pointer', fontSize: '12px', fontWeight: '700',
                }}
              >
                {copied === script.seed_id + '_2' ? '✅' : '📋 Copy'}
              </button>
              <pre style={{
                background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: '8px', padding: '14px', overflowX: 'auto',
                fontSize: '12px', color: '#94a3b8', lineHeight: '1.55',
                maxHeight: '400px', overflowY: 'auto', fontFamily: 'monospace',
                whiteSpace: 'pre',
              }}>
                {script.code}
              </pre>
            </div>
          )}
        </div>
      ))}
    </>
  );
}

// ── Transcripts Tab ────────────────────────────────────────────────────────
const CATEGORY_COLORS = {
  concept:   { color: THEME.colors.blue,  bg: 'rgba(0,212,255,0.08)',  icon: '📌' },
  rule:      { color: THEME.colors.green, bg: 'rgba(0,255,136,0.08)',  icon: '📏' },
  example:   { color: THEME.colors.gold,  bg: 'rgba(255,215,0,0.08)',  icon: '📖' },
  mistake:   { color: THEME.colors.red,   bg: 'rgba(255,51,51,0.08)',  icon: '❌' },
  checklist: { color: '#ff9900',          bg: 'rgba(255,153,0,0.08)',  icon: '✅' },
};

// ── Sample JSON template (for download) ───────────────────────────────────
const SAMPLE_JSON_TEMPLATE = {
  "source": "तुमच्या notes चे नाव — Topic (e.g. ICT Order Block Study / माझी Trading Rules)",
  "date": new Date().toISOString().slice(0, 10),
  "author": "तुमचे नाव किंवा Source (ICT / SMC / स्वतः)",
  "chunks": [
    {
      "category": "rule",
      "title": "OB + BOS Confirmation Rule",
      "content": "Order Block मध्ये enter करण्यापूर्वी BOS confirmation लागतो. CHoCH alone नाही चालत. OB zone मध्ये rejection wick + H1 BOS = valid entry. H4 bias align असणे mandatory.",
      "tags": ["ob", "bos", "entry", "rule", "xauusd"]
    },
    {
      "category": "rule",
      "title": "London Session BUY Rule",
      "content": "London open (09:00–11:00 IST) मध्ये Asia range low sweep झाल्यावर BUY setup बघायचा. SSL swept + OB zone + H1 BOS = Grade A entry. Risk 1%. Target previous day high.",
      "tags": ["london", "session", "buy", "ssl", "rule"]
    },
    {
      "category": "strategy",
      "title": "MTF OTE + Hammer Reversal Strategy",
      "content": "H4 bearish OB identify करा. H1 मध्ये OTE zone (0.618–0.786 fib) calculate करा. M15 मध्ये hammer candle wait करा. Entry: hammer close वर. SL: OB low च्या खाली 10 pips. TP1: FVG fill. TP2: next BSL. R:R minimum 1:2.",
      "tags": ["strategy", "ote", "hammer", "h4", "mtf", "reversal"]
    },
    {
      "category": "strategy",
      "title": "London Killzone SSL Sweep Strategy",
      "content": "Asia range identify करा (00:00–06:00 IST). Asia low = SSL level. London open (08:30–09:30 IST) मध्ये price SSL sweep करेल. Sweep नंतर rejection + M15 BOS = BUY. Entry: BOS candle close. SL: sweep low च्या खाली. TP: Asia high + BSL.",
      "tags": ["london", "ssl", "sweep", "strategy", "killzone"]
    },
    {
      "category": "concept",
      "title": "Order Block (OB) म्हणजे काय",
      "content": "Order Block = impulsive move आधीचा शेवटचा opposing candle. Bullish OB = bullish impulse आधीचा शेवटचा bearish candle. Bearish OB = bearish impulse आधीचा शेवटचा bullish candle. Institutional orders या zone मध्ये असतात. Fresh (unmitigated) OB = अजून price परत आला नाही.",
      "tags": ["ob", "concept", "institutional", "ict"]
    },
    {
      "category": "concept",
      "title": "Fair Value Gap (FVG) — Imbalance",
      "content": "3-candle pattern जिथे middle candle एक gap सोडतो. Candle 1 high ते Candle 3 low = bullish FVG (buy zone). Price 70-80% वेळा FVG fill करतो. FVG = magnet. OB + FVG overlap = strongest zone.",
      "tags": ["fvg", "imbalance", "concept", "fill"]
    },
    {
      "category": "checklist",
      "title": "Trade Entry Checklist",
      "content": "□ H4 bias confirm? (BUY/SELL) □ OB/FVG zone वर price आले? □ Liquidity swept? □ H1 BOS/CHoCH झाला? □ Session killzone आहे का? (London/NY) □ News नाही next 30 min? □ R:R minimum 1:2? □ Lot size risk 1% च्या आत?",
      "tags": ["checklist", "entry", "pre-trade", "rules"]
    },
    {
      "category": "checklist",
      "title": "Trade Management Checklist",
      "content": "□ SL placed? (OB low च्या खाली / swing low) □ TP1 set? (nearest FVG/swing high) □ TP2 set? (next liquidity) □ TP1 hit झाल्यावर 50% close □ SL breakeven ला move □ बाकी 50% TP2 साठी hold □ No revenge trade if SL hit",
      "tags": ["checklist", "management", "tp", "sl", "discipline"]
    },
    {
      "category": "example",
      "title": "XAU/USD London BUY Trade — A+ Grade",
      "content": "Date: 2026-06-15. H4 Bullish OB: $3282–$3296. London 09:15 IST: SSL swept at $3271. M15 BOS at $3284. Entry: $3285. SL: $3269 (16 pips). TP1: $3310 (25 pips = 1:1.5). TP2: $3340 (55 pips = 1:3.4). Result: TP2 hit. PnL: +$55. Grade: A+. Rules matched: OB + SSL sweep + London killzone.",
      "tags": ["example", "buy", "london", "xauusd", "a-grade"]
    },
    {
      "category": "mistake",
      "title": "FOMO Entry — Impulse Candle वर Entry",
      "content": "Mistake: Strong bullish candle दिसल्यावर लगेच BUY करणे. Problem: Institutional distribution सुरू असते. Fix: Strong impulse नंतर WAIT करा. OB retest होईपर्यंत थांबा. Rule: 'If you missed the move, miss it. Next setup येईल.'",
      "tags": ["mistake", "fomo", "impulse", "patience", "discipline"]
    },
    {
      "category": "mistake",
      "title": "SL Too Tight — Premature Stop Out",
      "content": "Mistake: OB zone मध्ये SL wick च्या आत ठेवणे. Problem: Price OB मध्ये wick करतो before reversing. Fix: SL = OB low च्या खाली 10-15 pips buffer. ATR(14) चा 50% = minimum SL buffer for XAU/USD.",
      "tags": ["mistake", "sl", "stop-loss", "wick", "buffer"]
    }
  ]
};

const CLAUDE_PROMPT = `You are a trading knowledge extractor for JARVIS Trading AI.

Extract ALL useful trading knowledge from the given content and return it as JARVIS JSON format.
Content can be in ANY language (Marathi, Hindi, English) — keep the content in the SAME language.

JARVIS JSON FORMAT (return ONLY this JSON, no markdown, no explanation):
{
  "source": "Source name — Topic",
  "date": "YYYY-MM-DD",
  "author": "Author/Channel name",
  "chunks": [
    {
      "category": "rule",
      "title": "Short rule name (same language as content)",
      "content": "Full explanation. Be specific. Include price levels, timeframes, percentages if mentioned.",
      "tags": ["keyword1", "keyword2", "keyword3"]
    }
  ]
}

CATEGORY OPTIONS (choose best fit):
- "rule"      → Actionable trading rule: WHEN condition + WHAT action. Must be specific.
- "strategy"  → Complete trading strategy with entry/SL/TP logic
- "concept"   → Key idea definition (what is it + why it matters for XAU/USD)
- "example"   → Real market example with symbol, timeframe, price levels, result
- "mistake"   → Common mistake + why it fails + how to fix
- "checklist" → Step-by-step list for pre-trade / trade management

EXTRACT EVERYTHING:
- Every rule mentioned → "rule" category
- Every strategy or system → "strategy" category
- Every concept explained → "concept" category
- Every trade example → "example" category
- Every mistake warned → "mistake" category
- Every checklist/steps → "checklist" category

IMPORTANT:
- Marathi content → keep in Marathi. English → keep in English.
- Be specific — vague statements are useless
- Include exact numbers, percentages, timeframes when mentioned
- Tags: lowercase English keywords only
- Return ONLY valid JSON. No markdown fences. No explanation.`;

// ── JSON Upload / Template Section ─────────────────────────────────────────
function JsonUploadSection({ onDone }) {
  const [status,      setStatus]      = useState(''); // '' | 'success' | 'error'
  const [msg,         setMsg]         = useState('');
  const [savedCount,  setSavedCount]  = useState(0);
  const [showPrompt,  setShowPrompt]  = useState(false);
  const [copied,      setCopied]      = useState(false);
  const fileRef = React.useRef(null);

  // Download template JSON
  const downloadTemplate = () => {
    const content = JSON.stringify(SAMPLE_JSON_TEMPLATE, null, 2);
    const blob = new Blob([content], { type: 'application/json' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = 'jarvis-knowledge-template.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  // Handle JSON file upload
  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.json')) {
      setStatus('error');
      setMsg('Fakt .json file upload kara.');
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const data = JSON.parse(ev.target.result);
        if (!data.chunks || !Array.isArray(data.chunks)) {
          setStatus('error');
          setMsg('Invalid format: "chunks" array nahi ahe. Template download kara ani Claude la dyaycha prompt vapar.');
          return;
        }
        let count = 0;
        const src = data.source || file.name.replace('.json', '');
        const validCats = ['concept', 'rule', 'example', 'mistake', 'checklist', 'strategy', 'system', 'psychology', 'note'];
        const catMap = { system: 'strategy', psychology: 'concept', note: 'concept' };
        data.chunks.forEach(chunk => {
          if (!chunk.title || !chunk.content) return;
          let cat = (chunk.category || 'concept').toLowerCase().trim();
          if (!validCats.includes(cat)) cat = 'concept'; // fallback instead of skip
          cat = catMap[cat] || cat;
          saveKnowledgeChunk({ source: src, category: cat, title: chunk.title, content: chunk.content, tags: chunk.tags || [] });
          count++;
        });
        setSavedCount(count);
        setStatus('success');
        setMsg(`"${src}" madhe ${count} knowledge chunks JARVIS memory madhe save zhale! Chat madhe automatically use honar.`);
        onDone?.();
      } catch {
        setStatus('error');
        setMsg('JSON parse error: file valid JSON ahe ka check kara.');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const copyPrompt = () => {
    navigator.clipboard.writeText(CLAUDE_PROMPT).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  return (
    <div style={{ marginBottom: '20px' }}>
      {/* Header — workflow explanation */}
      <div style={{ background: 'rgba(255,215,0,0.06)', border: '1px solid rgba(255,215,0,0.2)', borderRadius: '12px', padding: '16px', marginBottom: '14px' }}>
        <div style={{ fontSize: '13px', fontWeight: '800', color: THEME.colors.gold, marginBottom: '8px', letterSpacing: '1px' }}>
          🔄 ROJ CHI STUDY → JARVIS BRAIN (Workflow)
        </div>
        <div style={{ fontSize: '13px', color: THEME.colors.text, lineHeight: '1.9' }}>
          <b style={{ color: THEME.colors.gold }}>Step 1:</b> Template download kara (khali button)<br />
          <b style={{ color: THEME.colors.gold }}>Step 2:</b> Claude.ai la ja → Prompt copy karoon paste kara → Mag notes/transcript paste kara<br />
          <b style={{ color: THEME.colors.gold }}>Step 3:</b> Claude JSON generate karto → Copy kara → .json file madhe save kara<br />
          <b style={{ color: THEME.colors.gold }}>Step 4:</b> JARVIS madhe upload kara → Done! JARVIS shikla! 🧠
        </div>
      </div>

      {/* Action buttons row */}
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '12px' }}>
        {/* Download template */}
        <button
          style={{ ...S.btn(), padding: '10px 18px', fontSize: '13px', border: '1px solid rgba(255,215,0,0.3)', color: THEME.colors.gold, background: 'rgba(255,215,0,0.07)' }}
          onClick={downloadTemplate}
        >
          📥 Template Download Kara (.json)
        </button>

        {/* Upload JSON */}
        <button
          style={{ ...S.btn('primary'), padding: '10px 18px', fontSize: '13px' }}
          onClick={() => fileRef.current?.click()}
        >
          📤 JSON Upload Kara
        </button>
        <input ref={fileRef} type="file" accept=".json" style={{ display: 'none' }} onChange={handleFile} />

        {/* Claude prompt */}
        <button
          style={{ ...S.btn(), padding: '10px 18px', fontSize: '13px', border: '1px solid rgba(0,212,255,0.3)', color: THEME.colors.blue, background: 'rgba(0,212,255,0.07)' }}
          onClick={() => setShowPrompt(p => !p)}
        >
          🤖 Claude.ai Prompt {showPrompt ? '▲' : '▼'}
        </button>
      </div>

      {/* Status message */}
      {status === 'success' && (
        <div style={{ background: 'rgba(0,255,136,0.07)', border: '1px solid rgba(0,255,136,0.25)', borderRadius: '10px', padding: '12px 16px', marginBottom: '10px', fontSize: '13px', color: THEME.colors.green }}>
          ✅ {savedCount} chunks saved! {msg}
        </div>
      )}
      {status === 'error' && (
        <div style={{ background: 'rgba(255,51,51,0.07)', border: '1px solid rgba(255,51,51,0.25)', borderRadius: '10px', padding: '12px 16px', marginBottom: '10px', fontSize: '13px', color: THEME.colors.red }}>
          ❌ {msg}
        </div>
      )}

      {/* Claude.ai prompt panel */}
      {showPrompt && (
        <div style={{ background: 'rgba(0,212,255,0.04)', border: '1px solid rgba(0,212,255,0.2)', borderRadius: '12px', padding: '14px', marginBottom: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <div style={{ fontSize: '12px', fontWeight: '800', color: THEME.colors.blue, letterSpacing: '1.5px' }}>
              CLAUDE.AI LA DYAYCHA SYSTEM PROMPT
            </div>
            <button
              style={{ ...S.btn('primary'), padding: '6px 14px', fontSize: '12px', background: copied ? 'rgba(0,255,136,0.15)' : undefined, color: copied ? THEME.colors.green : '#000' }}
              onClick={copyPrompt}
            >
              {copied ? '✅ Copied!' : '📋 Copy Prompt'}
            </button>
          </div>
          <div style={{ fontSize: '12px', color: THEME.colors.textMuted, marginBottom: '10px', lineHeight: '1.6' }}>
            He prompt Claude.ai var paste kara (claude.ai/chat). Mag tumchi notes / transcript paste kara — Claude JSON format madhe extract karun deto. Tya JSON la copy karoon .json file madhe save kara ani ithe upload kara.
          </div>
          <pre style={{ background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '8px', padding: '12px', fontSize: '11px', color: '#94a3b8', lineHeight: '1.6', overflowX: 'auto', whiteSpace: 'pre-wrap', maxHeight: '260px', overflowY: 'auto' }}>
            {CLAUDE_PROMPT}
          </pre>
        </div>
      )}
    </div>
  );
}

function TranscriptImportForm({ onDone }) {
  const { extractTranscript, isThinking } = useJarvis();
  const [open,    setOpen]    = useState(false);
  const [source,  setSource]  = useState('');
  const [text,    setText]    = useState('');
  const [result,  setResult]  = useState(null);
  const [err,     setErr]     = useState('');

  const reset = () => { setSource(''); setText(''); setResult(null); setErr(''); };

  const handleExtract = async () => {
    if (!text.trim())   { setErr('Transcript text paste kara.'); return; }
    if (!source.trim()) { setErr('Source naam dya (e.g. ICT 2022 Mentorship).'); return; }
    setErr('');
    setResult(null);
    try {
      const r = await extractTranscript(text, source);
      setResult(r);
      onDone?.();
    } catch (e) {
      setErr('Extraction failed: ' + e.message);
    }
  };

  if (!open) return (
    <button
      style={{ ...S.btn('primary'), marginBottom: '16px', fontSize: '14px', padding: '10px 20px' }}
      onClick={() => setOpen(true)}
    >
      + YouTube Transcript Add Kara
    </button>
  );

  return (
    <div style={{ ...S.card, borderColor: 'rgba(0,212,255,0.25)', marginBottom: '20px', borderLeft: '3px solid rgba(0,212,255,0.5)' }}>
      <div style={S.sectionLabel}>NAVA TRANSCRIPT / YOUTUBE NOTES ADD KARA</div>
      <div style={{ fontSize: '12px', color: THEME.colors.textMuted, marginBottom: '12px', lineHeight: '1.6' }}>
        Rayner Teo, ICT, MentFX, Photon Trading — konahicha transcript paste kara. JARVIS automatically extract karto:<br />
        📌 Key Concepts · 📏 Rules · 📖 Examples · ❌ Mistakes to Avoid · ✅ Checklist
      </div>

      <input
        style={{ ...S.input, marginBottom: '10px' }}
        placeholder="Source naam (e.g. ICT 2022 Mentorship — OB Concepts, Rayner Teo — Price Action)"
        value={source}
        onChange={e => setSource(e.target.value)}
      />

      <textarea
        style={{ ...S.textarea, minHeight: '200px', marginBottom: '10px', fontSize: '13px' }}
        placeholder="Ithe YouTube transcript, video notes, ya kahi pan trading content paste kara...&#10;&#10;Example:&#10;'Today we're talking about Order Blocks. An order block is the last opposing candle before an impulse move...'&#10;&#10;JARVIS automatically concepts, rules, examples, mistakes, checklist extract karto."
        value={text}
        onChange={e => setText(e.target.value)}
      />

      {err && (
        <div style={{ color: THEME.colors.red, fontSize: '13px', marginBottom: '10px' }}>{err}</div>
      )}

      {result && (
        <div style={{ background: 'rgba(0,255,136,0.06)', border: '1px solid rgba(0,255,136,0.2)', borderRadius: '10px', padding: '14px', marginBottom: '12px' }}>
          <div style={{ fontSize: '15px', fontWeight: '700', color: THEME.colors.green, marginBottom: '8px' }}>
            ✅ {result.savedCount} knowledge chunks save zhale!
          </div>
          {result.source_summary && (
            <div style={{ fontSize: '13px', color: THEME.colors.text, lineHeight: '1.7', marginBottom: '10px' }}>
              {result.source_summary}
            </div>
          )}
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '10px' }}>
            {Object.entries(result.categories || {}).map(([cat, count]) => {
              const style = CATEGORY_COLORS[cat] || {};
              return (
                <span key={cat} style={{ padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '700', color: style.color, background: style.bg }}>
                  {CATEGORY_COLORS[cat]?.icon} {cat}: {count}
                </span>
              );
            })}
          </div>
          {result.rules?.length > 0 && (
            <div>
              <div style={{ fontSize: '11px', fontWeight: '700', color: THEME.colors.textMuted, letterSpacing: '1.5px', marginBottom: '6px' }}>EXTRACTED RULES:</div>
              {result.rules.slice(0, 3).map((r, i) => (
                <div key={i} style={{ background: 'rgba(255,255,255,0.03)', borderRadius: '6px', padding: '8px 10px', marginBottom: '6px', fontSize: '12px', borderLeft: `2px solid ${THEME.colors.green}` }}>
                  <div style={{ color: THEME.colors.green, fontWeight: '700' }}>📏 {r.title}</div>
                  <div style={{ color: THEME.colors.textMuted, marginTop: '3px' }}>{r.content?.slice(0, 120)}...</div>
                </div>
              ))}
            </div>
          )}
          <div style={{ fontSize: '12px', color: THEME.colors.textMuted, marginTop: '8px' }}>
            He chunks aata JARVIS chya memory madhe ahet. Chat madhe OB/FVG/etc vicharla ki JARVIS automatically relevant knowledge retrieve karto.
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        <button
          style={{ ...S.btn('primary'), opacity: isThinking ? 0.6 : 1 }}
          onClick={handleExtract}
          disabled={isThinking}
        >
          {isThinking ? '🤖 JARVIS Extract karto...' : '🤖 Extract & JARVIS Memory madhe Save Kara'}
        </button>
        <button style={S.btn()} onClick={() => { reset(); setOpen(false); }}>Cancel</button>
      </div>
    </div>
  );
}

function TranscriptsTab({ onAdded }) {
  const [chunks,    setChunks]   = useState(() => getKnowledgeChunks());
  const [filter,    setFilter]   = useState('all');
  const [search,    setSearch]   = useState('');
  const [expanded,  setExpanded] = useState(null);
  const [syncMsg,   setSyncMsg]  = useState('');
  const [syncing,   setSyncing]  = useState(false);

  const refresh = () => setChunks(getKnowledgeChunks());

  const handleDelete = (id) => { deleteKnowledgeChunk(id); refresh(); };

  // Auto-sync chunks → Rule Book + Behaviors
  const handleSync = () => {
    setSyncing(true);
    setSyncMsg('');
    setTimeout(() => {
      const { rulesAdded, behaviorsAdded } = syncChunksToRules();
      setSyncMsg(`✅ Done! ${rulesAdded} rules + ${behaviorsAdded} concepts added to JARVIS Brain.`);
      setSyncing(false);
    }, 300);
  };

  // Auto-sync on first load if chunks exist
  useEffect(() => {
    if (getKnowledgeChunks().length > 0) handleSync();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const categories = ['all', 'concept', 'rule', 'example', 'mistake', 'checklist'];

  const visible = chunks
    .filter(c => filter === 'all' || c.category === filter)
    .filter(c => !search || `${c.title} ${c.content} ${c.source}`.toLowerCase().includes(search.toLowerCase()));

  // Group by source for display
  const sources = [...new Set(chunks.map(c => c.source).filter(Boolean))];

  return (
    <>
      {/* JSON Upload + Claude Prompt panel */}
      <JsonUploadSection onDone={() => { refresh(); onAdded?.(); }} />

      {/* Divider */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
        <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.07)' }} />
        <span style={{ fontSize: '11px', color: THEME.colors.textMuted, letterSpacing: '1.5px' }}>YA RAW TRANSCRIPT PASTE KARA</span>
        <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.07)' }} />
      </div>

      <TranscriptImportForm onDone={() => { refresh(); onAdded?.(); }} />

      {chunks.length === 0 ? (
        <div style={S.emptyState}>
          <div style={{ fontSize: '32px', marginBottom: '12px' }}>🧠</div>
          <div style={{ fontSize: '15px', fontWeight: '700', color: THEME.colors.text, marginBottom: '8px' }}>
            Kahi nahi yet
          </div>
          <div style={{ lineHeight: '1.7' }}>
            "Nava Transcript Add Kara" click kara ani<br />
            ICT, Rayner Teo, MentFX, Photon Trading<br />
            yanche transcript paste kara.<br /><br />
            JARVIS automatically structured knowledge extract karto<br />
            ani chat madhe automatically use karto.
          </div>
        </div>
      ) : (
        <>
          {/* Stats bar + auto-sync status */}
          <div style={{ marginBottom: '14px' }}>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', padding: '10px 14px', background: 'rgba(0,212,255,0.04)', borderRadius: '10px', border: '1px solid rgba(0,212,255,0.1)' }}>
              <span style={{ fontSize: '12px', color: THEME.colors.textMuted }}>
                📚 {chunks.length} chunks from {sources.length} source(s):
              </span>
              {sources.map(s => (
                <span key={s} style={{ fontSize: '12px', color: THEME.colors.blue, background: 'rgba(0,212,255,0.08)', padding: '2px 8px', borderRadius: '10px' }}>
                  {s}
                </span>
              ))}
            </div>
            {syncMsg && (
              <div style={{ marginTop: '6px', fontSize: '12px', color: '#00ff88', padding: '6px 12px', background: 'rgba(0,255,136,0.06)', borderRadius: '7px', border: '1px solid rgba(0,255,136,0.15)' }}>
                🧠 Auto-sync: {syncMsg}
              </div>
            )}
          </div>

          {/* Filter + Search */}
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '10px' }}>
            {categories.map(cat => {
              const style = CATEGORY_COLORS[cat];
              const count = cat === 'all' ? chunks.length : chunks.filter(c => c.category === cat).length;
              return (
                <button key={cat} style={S.tab(filter === cat)} onClick={() => setFilter(cat)}>
                  {style?.icon || '🔍'} {cat} ({count})
                </button>
              );
            })}
          </div>
          <input style={S.searchInput} placeholder="Search chunks..." value={search} onChange={e => setSearch(e.target.value)} />

          {/* Chunks list */}
          {visible.length === 0 && (
            <div style={S.emptyState}>No chunks match this filter.</div>
          )}
          {visible.map(c => {
            const style = CATEGORY_COLORS[c.category] || {};
            return (
              <div key={c.id} style={{ ...S.card, borderLeft: `3px solid ${style.color || '#666'}` }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
                      <span style={S.badge(style.color, style.bg)}>{style.icon} {c.category}</span>
                      <span style={{ fontSize: '14px', fontWeight: '700', color: THEME.colors.text }}>{c.title}</span>
                    </div>
                    <div style={{ fontSize: '11px', color: THEME.colors.textMuted, marginBottom: '6px' }}>
                      📚 {c.source}
                    </div>
                    {expanded === c.id ? (
                      <div style={{ fontSize: '13px', color: THEME.colors.text, lineHeight: '1.7', whiteSpace: 'pre-wrap', marginBottom: '8px' }}>
                        {c.content}
                      </div>
                    ) : (
                      <div style={{ fontSize: '13px', color: THEME.colors.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '500px' }}>
                        {c.content}
                      </div>
                    )}
                    {c.tags?.length > 0 && (
                      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginTop: '6px' }}>
                        {c.tags.map(t => (
                          <span key={t} style={{ fontSize: '10px', padding: '1px 7px', borderRadius: '10px', background: 'rgba(255,255,255,0.05)', color: THEME.colors.textMuted }}>
                            #{t}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '5px', flexShrink: 0 }}>
                    <button style={{ ...S.btn(), padding: '4px 9px', fontSize: '12px' }} onClick={() => setExpanded(expanded === c.id ? null : c.id)}>
                      {expanded === c.id ? '▲' : '▼'}
                    </button>
                    <button style={{ ...S.btn('danger'), padding: '4px 9px', fontSize: '12px' }} onClick={() => handleDelete(c.id)}>✕</button>
                  </div>
                </div>
              </div>
            );
          })}
        </>
      )}
    </>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────
export default function KnowledgeBase() {
  const [tab, setTab]       = useState('transcripts');
  const [items, setItems]   = useState(() => getKnowledge());

  // Seed Pine Scripts into knowledge on first load
  useEffect(() => {
    PINE_SCRIPTS.forEach(s => seedKnowledgeScript(s));
    setItems(getKnowledge());
  }, []);

  const refresh = () => setItems(getKnowledge());
  const handleDelete = (id) => { deleteKnowledge(id); refresh(); };

  const chunkCount = getKnowledgeChunks().length;
  const tabs = [
    { id: 'transcripts', label: `🧠 Transcripts (${chunkCount})` },
    { id: 'behaviors',   label: `Behaviors (${getBehaviors().length})` },
    { id: 'patterns',    label: `Patterns (${getPatterns().length})` },
    { id: 'saved',       label: `Saved (${items.length})` },
    { id: 'ict',         label: 'ICT Library' },
    { id: 'scripts',     label: `📜 Pine Scripts (${PINE_SCRIPTS.length})` },
  ];

  return (
    <div style={S.page}>
      <div style={S.header}>
        <div style={S.title}>🧠 KNOWLEDGE BASE</div>
        <div style={S.subtitle}>Everything JARVIS has learned — behaviors, patterns, ICT concepts</div>
      </div>

      <div style={S.tabs}>
        {tabs.map(t => (
          <button key={t.id} style={S.tab(tab === t.id)} onClick={() => setTab(t.id)}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'transcripts' && <TranscriptsTab onAdded={refresh} />}
      {tab === 'behaviors'   && <BehaviorsTab />}
      {tab === 'patterns'    && <PatternsTab />}
      {tab === 'ict'         && <ICTLibraryTab />}
      {tab === 'scripts'     && <ScriptsTab onScriptAdded={refresh} />}
      {tab === 'saved' && (
        <>
          <AddKnowledgeForm onSaved={refresh} />
          {items.length === 0 && (
            <div style={S.emptyState}>
              No knowledge items saved. Add notes, articles, or observations here.
            </div>
          )}
          {items.map(item => (
            <KnowledgeCard key={item.id} item={item} onDelete={handleDelete} />
          ))}
        </>
      )}
    </div>
  );
}

